import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* =============================================================
   Jeden typ zadania, losuje 1 z 3 wariantów:
   - SUM_SQ_SUM  : suma = S  → min x^2+y^2
   - SUM_SQ_DIFF : różnica = D → min x^2+y^2 (traktujemy jako |x-y|=D)
   - SUM_CU_SUM  : suma = S  → min x^3+y^3
============================================================= */

/* ---------- utils ---------- */
function randEven(minEven, maxEven) {
  let a = minEven % 2 === 0 ? minEven : minEven + 1;
  let b = maxEven % 2 === 0 ? maxEven : maxEven - 1;
  const k = (b - a) / 2 + 1;
  return a + 2 * Math.floor(Math.random() * k);
}
function pickByWeight(items) {
  const total = items.reduce((s, it) => s + (it.w ?? 1), 0);
  let r = Math.random() * total;
  for (const it of items) {
    r -= (it.w ?? 1);
    if (r <= 0) return it.v;
  }
  return items[items.length - 1].v;
}
function parseIntLike(raw) {
  if (raw == null) return null;

  const val = typeof raw === "object" && raw.raw != null ? raw.raw : raw;
  const s = String(val).trim().replace(/\u2212/g, "-").replace(",", ".");
  if (!/^[+\-]?\d+$/.test(s)) return null;

  const v = parseInt(s, 10);
  return Number.isFinite(v) ? v : null;
}

/* ---------- warianty ---------- */
const VARIANTS = [
  { v: "SUM_SQ_SUM", w: 1 },
  { v: "SUM_SQ_DIFF", w: 1 },
  { v: "SUM_CU_SUM", w: 1 },
];

/* ---------- generator ---------- */
export function generateTask(options = {}) {
  const allowedVariant = VARIANTS.some(({ v }) => v === options.variant)
    ? options.variant
    : null;
  const variant = allowedVariant || pickByWeight(VARIANTS);

  if (variant === "SUM_SQ_SUM") {
    const S = randEven(8, 24);
    const h = S / 2;
    return {
      type: "OptimizationPairsTask",
      data: {
        variant,
        S,
        relationLatex: `x+y=${S}`,
        objective: "x^2+y^2",
        // jedna para optymalna, ale kolejność nie ma znaczenia
        solution: { x: h, y: h },
        maxPoints: 1,
      },
    };
  }

  if (variant === "SUM_SQ_DIFF") {
    const D = randEven(10, 30);
    const x = D / 2;
    const y = -D / 2;
    return {
      type: "OptimizationPairsTask",
      data: {
        variant,
        D,
        // w sprawdzaniu traktujemy jako |x-y|=D
        relationLatex: `|x-y|=${D}`,
        objective: "x^2+y^2",
        solution: { x, y },
        maxPoints: 1,
      },
    };
  }

  // SUM_CU_SUM
  const S = randEven(8, 24);
  const h = S / 2;
  return {
    type: "OptimizationPairsTask",
    data: {
      variant: "SUM_CU_SUM",
      S,
      relationLatex: `x+y=${S}`,
      objective: "x^3+y^3",
      solution: { x: h, y: h },
      maxPoints: 1,
    },
  };
}

/* ---------- TREŚĆ ---------- */
export function renderTask({ data }) {
  let content;
  if (data.variant === "SUM_SQ_SUM") {
    content = (
      <>
        Suma dwóch liczb jest równa <InlineMath math={String(data.S)} />. Znajdź
        takie dwie liczby, dla których suma ich kwadratów jest najmniejsza.
      </>
    );
  } else if (data.variant === "SUM_SQ_DIFF") {
    content = (
      <>
        Różnica dwóch liczb jest równa <InlineMath math={String(data.D)} />.
        Znajdź takie dwie liczby, dla których suma ich kwadratów jest najmniejsza.
      </>
    );
  } else {
    content = (
      <>
        Suma dwóch liczb jest równa <InlineMath math={String(data.S)} />. Znajdź
        takie dwie liczby, dla których suma ich sześcianów jest najmniejsza.
      </>
    );
  }
  return <div style={{ marginTop: 6, marginBottom: 6 }}>{content}</div>;
}

/* ---------- input: dwie liczby (bez sugerowania x/y w treści) ---------- */
export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer ?? { x: { raw: "" }, y: { raw: "" } };
  const setField = (key) => (v) => onChange({ ...(ua || {}), [key]: v });

  return (
    <div style={{ display: "grid", gap: 12, marginTop: 8, maxWidth: 420 }}>
      <div>Wpisz dwie liczby spełniające warunek z treści:</div>

      <div style={{ display: "grid", gap: 8 }}>
        <div>Podaj <strong>pierwszą liczbę</strong>:</div>
        <FractionInput
          value={ua.x}
          onChange={setField("x")}
          disabled={disabled}
          label={"x"}          // <- podgląd w FractionInput: x = ...
          integersOnly
        />
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        <div>Podaj <strong>drugą liczbę</strong>:</div>
        <FractionInput
          value={ua.y}
          onChange={setField("y")}
          disabled={disabled}
          label={"y"}          // <- podgląd w FractionInput: y = ...
          integersOnly
        />
      </div>
    </div>
  );
}


/* ---------- sprawdzanie (akceptuje zamianę miejscami) ---------- */
function isValidAndOptimal(data, x, y) {
  // warunek z treści
  if (data.variant === "SUM_SQ_SUM" || data.variant === "SUM_CU_SUM") {
    if (x + y !== data.S) return false;
  } else {
    // różnica jako wartość bezwzględna: |x-y| = D
    if (Math.abs(x - y) !== data.D) return false;
  }

  // optimum: konkretna para (kolejność dowolna)
  const sol = data.solution;
  const direct = x === sol.x && y === sol.y;
  const swapped = x === sol.y && y === sol.x;
  return direct || swapped;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  const x = parseIntLike(userAnswer?.x);
  const y = parseIntLike(userAnswer?.y);
  if (x == null || y == null) return false;

  return isValidAndOptimal(data, x, y);
}

/* ---------- poprawna odpowiedź (bez punktu) ---------- */
export function renderAnswer({ data }) {
  const { x, y } = data.solution;
  return (
    <div style={{ marginTop: 6, display: "grid", rowGap: 4 }}>
      <div>
        <InlineMath math={`x=${x}`} />
      </div>
      <div>
        <InlineMath math={`y=${y}`} />
      </div>
    </div>
  );
}

/* ---------- wyjaśnienie krok po kroku (zostawiam jak było, tylko poprawny krok 3) ---------- */
export function renderExplanation({ data }) {
  const wrapInt = (n) => (n < 0 ? `(${n})` : String(n));

  let k1a, k1b, k2def, k2exp, k3, wniosek;

  if (data.variant === "SUM_SQ_SUM") {
    const S = data.S;

    k1a = String.raw`x+y=${S}.`;
    k1b = String.raw`\Rightarrow\ y=${S}-x.`;

    k2def = String.raw`f(x)=x^2+(${S}-x)^2.`;
    k2exp = String.raw`\begin{aligned}
f(x)&=x^2+\bigl(${S}-x\bigr)^2\\
&=x^2+\bigl(${S}^2-2\cdot ${S}\,x+x^2\bigr)\\
&=2x^2-2\cdot ${S}\,x+${S}^2.
\end{aligned}`;

    k3 = String.raw`\begin{aligned}
x_w&=-\frac{b}{2a}=-\frac{-2\cdot ${S}}{2\cdot 2}=\frac{${S}}{2}=${S / 2},\\
y_w&=${S}-x_w=\frac{${S}}{2}=${S / 2}.
\end{aligned}`;

    wniosek = String.raw`\Rightarrow\ x=${S / 2},\ \ y=${S / 2}.`;
  } else if (data.variant === "SUM_SQ_DIFF") {
    const D = data.D;

    k1a = String.raw`|x-y|=${D}.`;
    k1b = String.raw`\text{Możemy przyjąć np. }y=x-${D}\ \text{(drugi przypadek daje te same liczby w innej kolejności).}`;

    k2def = String.raw`f(x)=x^2+(x-${D})^2.`;
    k2exp = String.raw`\begin{aligned}
f(x)&=x^2+\bigl(x-${D}\bigr)^2\\
&=x^2+\bigl(x^2-2\cdot ${D}\,x+${D}^2\bigr)\\
&=2x^2-2\cdot ${D}\,x+${D}^2.
\end{aligned}`;

    k3 = String.raw`\begin{aligned}
x_w&=-\frac{b}{2a}=-\frac{-2\cdot ${D}}{2\cdot 2}=\frac{${D}}{2}=${D / 2},\\
y_w&=x_w-${D}=\frac{${D}}{2}-${D}=-\frac{${D}}{2}=${-D / 2}.
\end{aligned}`;

    wniosek = String.raw`\Rightarrow\ x=\frac{${D}}{2},\ \ y=-\frac{${D}}{2}\ \ \text{(kolejność liczb nie ma znaczenia).}`;
  } else {
    const S = data.S;

    k1a = String.raw`x+y=${S}.`;
    k1b = String.raw`\Rightarrow\ y=${S}-x.`;

    k2def = String.raw`f(x)=x^3+(${S}-x)^3.`;
    k2exp = String.raw`\begin{aligned}
f(x)&=x^3+\bigl(${S}-x\bigr)^3\\
&=x^3+\bigl(${S}^3-3\cdot ${S}^2 x+3\cdot ${S}\,x^2-x^3\bigr)\\
&=3\cdot ${S}\,x^2-3\cdot ${S}^2 x+${S}^3.
\end{aligned}`;

    k3 = String.raw`\begin{aligned}
x_w&=-\frac{b}{2a}=-\frac{-3\cdot ${S}^2}{2\cdot (3\cdot ${S})}
=\frac{${S}}{2}=${S / 2},\\
y_w&=${S}-x_w=\frac{${S}}{2}=${S / 2}.
\end{aligned}`;

    wniosek = String.raw`\Rightarrow\ x=${S / 2},\ \ y=${S / 2}.`;
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div>
        <strong>Krok 1.</strong> Zapis z treści i przekształcenie do zależności:
      </div>
      <BlockMath math={k1a} />
      <BlockMath math={k1b} />

      <div>
        <strong>Krok 2.</strong> Budujemy funkcję <InlineMath math={"f(x)"} /> i dokładnie ją rozwijamy:
      </div>
      <BlockMath math={k2def} />
      <BlockMath math={k2exp} />

      <div>
        <strong>Krok 3.</strong> Wierzchołek funkcji kwadratowej:
      </div>
      <BlockMath math={k3} />

      <div>
        <strong>Wniosek.</strong>
      </div>
      <BlockMath math={wniosek} />
    </div>
  );
}

/* ---------- podgląd odpowiedzi użytkownika (bez punktu) ---------- */
export function renderUserAnswer(taskOrAnswer, maybeAnswer) {
  // Obsłuż oba warianty wywołania:
  // - renderUserAnswer(task, answer)
  // - renderUserAnswer(answer)
  const answer = maybeAnswer ?? taskOrAnswer;

  const show = (v) => {
    const raw = typeof v === "object" && v?.raw != null ? v.raw : v;
    const s = String(raw ?? "").trim();
    return s === "" ? null : s;
  };

  if (!answer) return <i>(brak odpowiedzi)</i>;

  const xVal = show(answer?.x);
  const yVal = show(answer?.y);

  if (!xVal && !yVal) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ marginTop: 6, display: "grid", rowGap: 4 }}>
      <div><InlineMath math={`x=${xVal ?? "?"}`} /></div>
      <div><InlineMath math={`y=${yVal ?? "?"}`} /></div>
    </div>
  );
}


export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
